<template>
  <div class="modal-card">
    <header class="modal-card-head">
      <p class="modal-card-title">Create Question</p>
    </header>
    <div class="modal-card-body px-5">
      <!--Loading-->
      <b-loading :is-full-page="true" :active.sync="isLoading"></b-loading>
      <b-field label="Type">
        <b-select
          ref="question_type"
          required
          expanded
          placeholder="Specify type of question"
          v-model="new_question.type"
        >
          <option value="MCQ">Multiple choice</option>
          <option value="DQ">Descriptive/Short Answer</option>
          <option value="TF">True/False</option>
          <option value="CL">Custom Layout</option>
          <option value="DRAGDROP">Drag & Drop</option>
          <option value="PATTERN">Pattern</option>
          <option value="ORDERING">Ordering</option>
          <option value="DIVIDER">Divider</option>
          <option value="MATCHING">Match the Following</option>
          <option value="NONE">None</option>
        </b-select>
      </b-field>

      <!--Primary Question-->
      <p class="pt-2 title is-6">Primary Question</p>
      <basic-quill ref="question" v-model="new_question.question"></basic-quill>

      <hr />

      <b-collapse
        class="card"
        animation="slide"
        v-for="(part_collapse, index) of part_collapses"
        :key="index"
        :open="partCollapsesIsOpen == index"
        @open="partCollapsesIsOpen = index"
      >
        <template #trigger="props">
          <div class="card-header" role="button">
            <p class="card-header-title">
              {{ part_collapse.title }}
            </p>
            <a class="card-header-icon">
              <b-icon :icon="props.open ? 'menu-down' : 'menu-up'"> </b-icon>
            </a>
          </div>
        </template>
        <div class="card-content">
          <div class="content">
            <!--For Clipart Layers-->
            <set-clipart-layer
              v-if="part_collapse.type == 'CLIPART_LAYER'"
              :show_track_count="true"
              :show_add_remove="true"
              v-model="new_question.layers"
            ></set-clipart-layer>

            <!--For Story Layers-->
            <set-story
              v-if="part_collapse.type == 'STORIES'"
              v-model="new_question.stories"
            ></set-story>

            <!--Adding tables-->
            <set-tables
              v-if="part_collapse.type == 'TABLES'"
              v-model="new_question.tables"
            ></set-tables>

            <!--Set Number Line-->
            <set-number-line
              v-if="part_collapse.type == 'NUMBER_LINE'"
              v-model="new_question.number_line"
            ></set-number-line>

            <!--Set Clock-->
            <set-clock
              v-if="part_collapse.type == 'CLOCK'"
              v-model="new_question.clock"
            ></set-clock>

            <!--Set Protractor-->
            <set-protractor
              v-if="part_collapse.type == 'PROTRACTOR'"
              v-model="new_question.protractor"
            ></set-protractor>

            <!-- Set BarChart -->
            <set-bar-chart
              v-if="part_collapse.type == 'BARCHART'"
              v-model="new_question.bar_chart"
            ></set-bar-chart>

            <!-- Set LineChart -->
            <set-line-chart
              v-if="part_collapse.type == 'LINECHART'"
              v-model="new_question.line_chart"
            ></set-line-chart>

            <!-- Set VeinnDiagram -->
            <set-venn-diagram
              v-if="part_collapse.type == 'VENNDIAGRAM'"
              v-model="new_question.venn_diagram"
            ></set-venn-diagram>

            <!-- Set DragBox -->
            <set-drag-box
              v-if="part_collapse.type == 'DRAGBOX'"
              v-model="new_question.drag_box"
            ></set-drag-box>

            <!-- Set ColorGrid -->
            <set-color-grid
              v-if="part_collapse.type == 'COLORGRID'"
              v-model="new_question.color_grid"
            ></set-color-grid>

            <!-- Set GridTranslator -->
            <set-grid-translator
              v-if="part_collapse.type == 'GRIDTRANSLATOR'"
              v-model="new_question.grid_translator"
            ></set-grid-translator>

            <!-- Set AngleLines -->
            <set-angle-lines
              v-if="part_collapse.type == 'ANGLELINES'"
              v-model="new_question.angle_lines"
            ></set-angle-lines>

            <!-- Set LineCreator -->
            <set-line-creator
              v-if="part_collapse.type == 'LINECREATOR'"
              v-model="new_question.line_creator"
            ></set-line-creator>

            <!-- Set CutSpinner -->
            <set-cut-spinner
              v-if="part_collapse.type == 'CUTSPINNER'"
              v-model="new_question.cut_spinner"
            ></set-cut-spinner>

            <!-- Set ClipartTable -->
            <set-clipart-table
              v-if="part_collapse.type == 'CLIPARTTABLE'"
              v-model="new_question.clipart_table"
            ></set-clipart-table>

            <!-- Set ClipartMeasurement -->
            <set-clipart-measurement
              v-if="part_collapse.type == 'CLIPARTMEASUREMENT'"
              v-model="new_question.clipart_measurement"
            ></set-clipart-measurement>

            <!-- Set Thermometer -->
            <set-thermometer
              v-if="part_collapse.type == 'THERMOMETER'"
              v-model="new_question.thermometer"
            ></set-thermometer>

            <!-- Set Shapes -->
            <set-shapes
              v-if="part_collapse.type == 'SHAPES'"
              v-model="new_question.shapes"
            ></set-shapes>

            <!-- Set Ruler -->
            <set-ruler
              v-if="part_collapse.type == 'RULER'"
              v-model="new_question.ruler"
            ></set-ruler>
          </div>
        </div>
      </b-collapse>

      <!--Secondary Question-->
      <p class="pt-2 title is-6">Secondary Question</p>
      <basic-quill
        ref="question1"
        v-model="new_question.question1"
      ></basic-quill>
      <!-- <b-field label="Secondary Question">
          <b-input ref="question1" placeholder="Add your secondary question here" type="textarea" v-model="new_question.question1"></b-input>
        </b-field> -->

      <b-field label="Secondary Question Position">
        <b-select v-model="new_question.question1_position">
          <option :value="null">Default</option>
          <option value="after1">After 1</option>
        </b-select>
      </b-field>

      <hr />

      <!--For Divide Type Questions-->
      <set-divider
        v-if="new_question.type == 'DIVIDER'"
        v-model="new_question.divider"
      ></set-divider>

      <!--For Matching Type Questions-->
      <set-matching
        v-if="new_question.type == 'MATCHING'"
        v-model="new_question.matching"
      ></set-matching>

      <!--For PATTERN-->
      <div v-if="new_question.type == 'PATTERN'" class="columns is-multiline">
        <div class="column is-12">
          <p class="title is-6">Pattern</p>
        </div>
        <div class="column is-5">
          <div class="columns">
            <div class="column is-9">
              <b-pagination
                :total="fetchCliparts.length"
                v-model="current_clipart_page"
                :per-page="clipart_page_items"
                aria-next-label="Next page"
                aria-previous-label="Previous page"
                aria-page-label="Page"
                aria-current-label="Current page"
              >
              </b-pagination>
            </div>
            <div class="column is-3">
              <b-numberinput
                v-model="current_clipart_page"
                :controls="false"
              ></b-numberinput>
            </div>
          </div>
          <div class="columns is-multiline">
            <div
              v-for="clipart in currentPageCliparts"
              :key="clipart._id"
              style="position: relative; border: 1px solid #ccc"
              class="column is-2 has-text-centered"
            >
              <show-image width="80" :src="clipart.file" />
              <p>
                <b-button
                  @click="selectPatternClipart(clipart)"
                  type="is-info"
                  size="is-small"
                  outlined
                  >Select</b-button
                >
              </p>
            </div>
          </div>
        </div>

        <div class="column is-7">
          <div class="columns is-multiline">
            <div
              v-for="(clipart, clipi) in new_question.pattern.cliparts"
              :key="`PATTERN_${clipart._id}`"
              style="position: relative; border: 1px solid #ccc"
              class="column is-2 has-text-centered"
            >
              <show-image width="80" :src="clipart.file" />
              <p>Clipart {{ clipi }}</p>
              <p>
                <b-button
                  @click="
                    removePatternClipart({ id: clipart._id, index: clipi })
                  "
                  type="is-danger"
                  size="is-small"
                  outlined
                  >Remove</b-button
                >
              </p>
            </div>
          </div>

          <div class="columns is-multiline">
            <div class="my-1 column is-12">
              <b-button @click="addPatternLayer()">Add Layer</b-button>
            </div>
            <div
              v-for="(pat_layer, plindex) in new_question.pattern.layers"
              :key="`PATTERN_LAYER_${plindex}`"
              style="background: #ccc"
              class="my-1 column is-12"
            >
              <div class="columns is-multiline">
                <div class="column is-4">
                  <b-field label="Type">
                    <b-select v-model="pattern_column.type" expanded>
                      <option value="DISPLAY">Display</option>
                      <option value="USERINP">User Input</option>
                    </b-select>
                  </b-field>
                </div>
                <div class="column is-4">
                  <b-field label="Clipart">
                    <b-select v-model="pattern_column.clipart" expanded>
                      <option :value="-1">NONE</option>
                      <option
                        v-for="(pattern_clipart, pcindex) in new_question
                          .pattern.cliparts"
                        :key="`PROW_${plindex}_${pattern_clipart._id}`"
                        :value="pcindex"
                      >
                        Clipart {{ pcindex }}
                      </option>
                    </b-select>
                  </b-field>
                </div>
                <div class="column is-4">
                  <b-button @click="addPatternColumn(plindex)"
                    >Add Column</b-button
                  >
                  <b-button type="is-danger" @click="removePatternLayer()"
                    >Remove Layer</b-button
                  >
                </div>
                <div class="column is-12">
                  <div
                    v-for="(column, col_index) in pat_layer.columns"
                    :key="`PROW_${plindex}_COL_${col_index}`"
                    :style="`position: relative;border: 1px solid #000;float:left;display:flex;align-items:center;justify-content:center;width:${
                      column.width
                    }px;height:${column.width}px;${
                      column.type == 'USERINP' ? 'background:#5252ff;' : ''
                    }`"
                    class="m-1"
                  >
                    <show-image
                      style="height: 90%; width: 100%"
                      v-if="column.type == 'DISPLAY' && column.clipart != -1"
                      :src="new_question.pattern.cliparts[column.clipart].file"
                    />
                    <show-image
                      style="height: 50%; width: 50%"
                      v-if="column.type == 'USERINP' && column.clipart != -1"
                      :src="new_question.pattern.cliparts[column.clipart].file"
                    />

                    <span
                      @click="removePatternColumn(plindex, col_index)"
                      style="
                        position: absolute;
                        right: 0;
                        top: 0;
                        background: red;
                        color: white;
                        padding: 2px 10px;
                      "
                      >X</span
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!--For Pattern end-->

      <!--Drag & Drop-->
      <p v-if="new_question.type == 'DRAGDROP'" class="title is-6">
        Drag & Drop
      </p>
      <div
        v-if="new_question.type == 'DRAGDROP'"
        class="columns is-centered is-multiline"
      >
        <div class="column is-6">
          <b-field label="Select Background 1">
            <b-select
              v-model="new_question.drag_drop.background"
              placeholder="Select image"
              expanded
            >
              <option
                v-for="pl in fetchPictureLibraries"
                :value="pl._id"
                :key="pl._id"
              >
                {{ pl.name }}
              </option>
            </b-select>
          </b-field>
          <!--Show BG-->
          <show-image
            v-if="new_question.drag_drop.background"
            :src="plPath(new_question.drag_drop.background)"
          />
        </div>
        <div class="column is-6">
          <b-field label="Select Background 2">
            <b-select
              v-model="new_question.drag_drop.background1"
              placeholder="Select image"
              expanded
            >
              <option
                v-for="pl in fetchPictureLibraries"
                :value="pl._id"
                :key="pl._id"
              >
                {{ pl.name }}
              </option>
            </b-select>
          </b-field>
          <!--Show BG-->
          <show-image
            v-if="new_question.drag_drop.background1"
            :src="plPath(new_question.drag_drop.background1)"
          />
        </div>
        <div class="column is-12">
          <p class="title is-6">
            Layers
            <b-button
              @click="addDragDropLayer()"
              type="is-success"
              size="is-small"
              icon-left="plus"
            ></b-button>
          </p>
          <b-collapse
            class="card"
            animation="slide"
            v-for="(layer, index) of new_question.drag_drop.layers"
            :key="`DRAG_DROP_LAYER_${index}`"
            :open="isDragDropOpen == index"
            @open="isDragDropOpen = index"
          >
            <template #trigger="props">
              <div class="card-header" role="button">
                <p class="card-header-title">{{ `Layer ${index + 1}` }}</p>
                <a class="card-header-icon">
                  <b-icon :icon="props.open ? 'menu-down' : 'menu-up'"></b-icon>
                </a>
              </div>
            </template>
            <div class="card-content">
              <div class="columns is-multiline">
                <div class="column is-12">
                  <b-button
                    type="is-danger"
                    @click="removeDragDropLayer('DRAGDROP', index)"
                    >Remove</b-button
                  >
                </div>
                <div class="column is-5">
                  <div class="columns">
                    <div class="column is-12">
                      <b-pagination
                        :total="fetchCliparts.length"
                        v-model="current_clipart_page"
                        :per-page="clipart_page_items"
                        aria-next-label="Next page"
                        aria-previous-label="Previous page"
                        aria-page-label="Page"
                        aria-current-label="Current page"
                      >
                      </b-pagination>
                    </div>
                  </div>
                  <div class="columns is-multiline">
                    <div
                      v-for="clipart in currentPageCliparts"
                      :key="clipart._id"
                      style="position: relative; border: 1px solid #ccc"
                      class="column is-2 has-text-centered"
                    >
                      <show-image width="80" :src="clipart.file" />
                      <p>
                        <b-button
                          @click="selectClipartDragDrop(index, clipart)"
                          type="is-info"
                          size="is-small"
                          outlined
                          >Select</b-button
                        >
                      </p>
                    </div>
                  </div>
                </div>
                <div class="column is-7">
                  <b-field grouped>
                    <b-field label="No. of images" expanded>
                      <b-numberinput
                        max="10"
                        size="is-small"
                        v-model="layer.number"
                      ></b-numberinput>
                    </b-field>
                    <b-field label="No. of images drag" expanded>
                      <b-numberinput
                        max="10"
                        size="is-small"
                        v-model="layer.number_drag"
                      ></b-numberinput>
                    </b-field>
                    <b-field label="Drag to Background" expanded>
                      <b-select
                        expanded
                        size="is-small"
                        v-model="layer.background_drag"
                      >
                        <option value="BG1">Background 1</option>
                        <option value="BG2">Background 2</option>
                      </b-select>
                    </b-field>
                  </b-field>

                  <p class="title is-6">View</p>

                  <div class="columns is-gapless">
                    <div
                      :class="`column has-text-centered is-flex ${$common.clipartLayerAlignmentClass(
                        layer.alignment
                      )}`"
                    >
                      <div>
                        <div
                          v-for="(clipart, i) in layer.number"
                          :key="`${i}`"
                          :style="`width:60px`"
                          class="default-clipart m-1"
                        >
                          <show-image
                            v-if="layer.clipart"
                            :src="layer.clipart.file"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </b-collapse>
        </div>
      </div>

      <!--Custom Layout if CL type or MCQ with custom layout-->
      <set-custom-layout
        v-if="
          new_question.type == 'CL' ||
          (new_question.mcq && new_question.mcq.has_custom_layout)
        "
        v-model="new_question.custom_layout"
        :question_type="new_question.type"
      ></set-custom-layout>

      <set-mcq
        v-if="new_question.type == 'MCQ'"
        v-model="new_question.mcq"
        :question_type="new_question.type"
      />
      <set-mcq
        v-if="new_question.type == 'ORDERING'"
        v-model="new_question.ordering"
        :question_type="new_question.type"
      />

      <!--For Descriptive-->
      <div v-else-if="new_question.type == 'DQ'">
        <b-field label="Answer">
          <b-input required v-model="new_question.descriptive.answer"></b-input>
        </b-field>
        <b-checkbox v-model="new_question.descriptive.answer_not_exactly_same"
          >Answer Not Exactly Same</b-checkbox
        >
      </div>

      <!--For True/False-->
      <b-field v-else-if="new_question.type == 'TF'" label="Correct Answer">
        <b-switch
          min="1"
          v-model="new_question.tf.answer"
          type="is-success"
          passive-type="is-danger"
          >{{ new_question.tf.answer }}</b-switch
        >
      </b-field>

      <!--Explanation for wrong answer-->
      <p class="pt-2 title is-6">Explanation (for wrong answer)</p>
      
      <!-- HTML Editor Explanation -->
      <input
        v-show="false"
        type="file"
        ref="new_explanation_file_inp"
        @change="changeExplanationFile()"
      />
      <quill-editor
        class="quiz-description-box"
        ref="myQuillEditor"
        v-model="new_question.explanation"
        :options="editorOption"
      />

      <!-- Custom Layout Explanation -->
      <div class="mt-4">
        <p class="pt-2 title is-6">Custom Layout Explanation</p>
        <set-custom-layout
          v-model="new_question.explanation_custom_layout"
          :question_type="'EXPLANATION'"
        ></set-custom-layout>
      </div>

      <div style="padding-top: 10px">
        <b-button @click="addQuestion()" class="btn-center" type="is-dark"
          >Save</b-button
        >
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters, mapActions } from "vuex";

// Components
const {getImageURL} = require("@/components/General/utils")
import ShowImage from "@/components/General/ShowImage";
import SetClipartLayer from "@/components/Question/SetClipartLayer";
import SetStory from "@/components/Question/SetStory";
import SetNumberLine from "@/components/Question/NumberLine/SetNumberLine";
import SetClock from "@/components/Question/Clock/SetClock";
import SetProtractor from "@/components/Question/Protractor/SetProtractor";
import SetDivider from "@/components/Question/SetDivider";
import SetMatching from "@/components/Question/Matching/SetMatching";
import SetMcq from "@/components/Question/MCQ/SetMcq";
import SetCustomLayout from "@/components/Question/CustomLayout/SetCustomLayout";
import { returnNewCLOLayer } from "@/components/Question/CustomLayout/utils";
import BasicQuill from "@/components/Editor/BasicQuill";
// import FocusButton from '@/components/FocusButton'
import SetTables from "@/components/Question/SetTables";
import SetBarChart from "@/components/Question/Charts/SetBarChart";
import SetLineChart from "@/components/Question/Charts/SetLineChart";
import SetVennDiagram from "@/components/Question/VeinnDiagram/SetVennDiagram";
import SetDragBox from "@/components/Question/DragBox/SetDragBox";
import SetGridTranslator from "@/components/Question/GridTranslator/SetGridTranslator";
import SetAngleLines from "@/components/Question/AngleLines/SetAngleLines";
import SetLineCreator from '@/components/Question/LineCreator/SetLineCreator';
import SetCutSpinner from "@/components/Question/CutSpinner/SetCutSpinner";
import SetColorGrid from "@/components/Question/ColorGrid/SetColorGrid";
import SetClipartTable from "@/components/Question/ClipartTable/SetClipartTable";
import SetClipartMeasurement from "@/components/Question/ClipartMeasurement/SetClipartMeasurement";
import SetThermometer from "@/components/Question/Thermometer/SetThermometer";
import SetShapes from "@/components/Question/Shapes/SetShapes";
import SetRuler from "@/components/Question/Ruler/SetRuler";

//Vue-Quill Editor
import { quillEditor } from "vue-quill-editor";
import { formatHTMLString } from "@/components/Editor/utils";
import "quill/dist/quill.core.css"; // import styles
import "quill/dist/quill.snow.css"; // for snow theme
import "quill/dist/quill.bubble.css"; // for bubble theme
const toolbarOptions = [
  ["bold", "italic", "underline", "strike"], // toggled buttons
  ["blockquote", "code-block"],
  [{ header: 1 }, { header: 2 }], // custom button values
  [{ list: "ordered" }, { list: "bullet" }],
  [{ script: "sub" }, { script: "super" }], // superscript/subscript
  [{ indent: "-1" }, { indent: "+1" }], // outdent/indent
  [{ direction: "rtl" }], // text direction
  [{ size: ["small", false, "large", "huge"] }], // custom dropdown
  [{ header: [1, 2, 3, 4, 5, 6, false] }],
  [{ color: [] }, { background: [] }], // dropdown with defaults from theme
  [{ font: [] }],
  [{ align: [] }],
  ["link", "image", "video"],
  ["clean"],
];

export default {
  name: "add-question",
  components: {
    ShowImage,
    quillEditor,
    SetClipartLayer,
    SetTables,
    SetStory,
    SetNumberLine,
    SetClock,
    SetProtractor,
    SetDivider,
    SetMatching,
    SetCustomLayout,
    BasicQuill,
    SetMcq,
    // FocusButton
    SetBarChart,
    SetLineChart,
    SetVennDiagram,
    SetDragBox,
    SetColorGrid,
    SetGridTranslator,
    SetAngleLines,
    SetLineCreator,
    SetCutSpinner,
    SetClipartTable,
    SetClipartMeasurement,
    SetThermometer,
    SetShapes,
    SetRuler
  },
  props: {
    skill_id: {
      type: String,
      required: true,
    },
    question_id: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      current_clip: null,
      modalAddQuestionActive: false,
      explanation_file: null,
      editorOption: {
        modules: {
          toolbar: {
            container: toolbarOptions,
            handlers: {
              image: (value) => {
                if (value) {
                  this.$refs["new_explanation_file_inp"].click();
                } else {
                  this.quill.format("image", false);
                }
              },
            },
          },
        },
      },

      isOpen: 0,
      isOpenCustomLayerClipart: 0,
      isDragDropOpen: 0,
      isLoading: false,
      difficulties: [
        {
          name: "Easy",
          value: 1,
        },
        {
          name: "Medium",
          value: 2,
        },
        {
          name: "Hard",
          value: 3,
        },
      ],
      toggle_custom_layout_cl_drawer: false,
      // Clipart layer object
      new_layer: {
        clipart: null,
        number: 5,
        number_row: 5,
        clipart_resize_type: "W",
        clipart_width: 80,
        clipart_height: 80,
        layer_width: 100,
        alignment: "C",
        is_count_tracking: false,
        count_tracking_step: 1,
      },
      new_layer_drag_drop: {
        clipart: null,
        number: 1,
        number_drag: 1,
        background_drag: null,
      },
      pattern_layer: {
        columns: [],
      },
      pattern_column: {
        type: null,
        clipart: null,
        width: 80,
      },
      current_clipart_page: 1,
      clipart_page_items: 50,
      partCollapsesIsOpen: null,
      part_collapses: [
        { title: "Cliparts", type: "CLIPART_LAYER" },
        { title: "Tables", type: "TABLES" },
        { title: "Stories", type: "STORIES" },
        { title: "Number Line", type: "NUMBER_LINE" },
        { title: "Clock", type: "CLOCK" },
        { title: "Protractor", type: "PROTRACTOR" },
        { title: "Bar Chart", type: "BARCHART" },
        { title: "Line Chart", type: "LINECHART" },
        { title: "Veinn Diagram", type: "VENNDIAGRAM" },
        { title: "Drag Box", type: "DRAGBOX" },
        { title: "Color Grid", type: "COLORGRID" },
        { title: "Grid Translator", type: "GRIDTRANSLATOR" },
        { title: "Angle Lines", type: "ANGLELINES" },
        { title: "Line Creator", type: "LINECREATOR" },
        { title: "Cut Spinner", type: "CUTSPINNER" },
        { title: "Clipart Table", type: "CLIPARTTABLE" },
        { title: "Clipart Measurement", type: "CLIPARTMEASUREMENT" },
        { title: "Thermometer", type: "THERMOMETER" },
        { title: "Shapes", type: "SHAPES" },
        { title: "Ruler", type: "RULER" },
      ],
      new_question: {
        question: "",
        question1: "",
        question1_position: null,
        layers: [],
        tables: [],
        stories: [],
        files: [],
        editfiles: [],
        whiteboard_svg_code: "",
        type: null,
        difficulty: 1,
        explanation: "",
        explanation_custom_layout: null,
        custom_layout: null,
        drag_drop: { background: null, background1: null, layers: [] },
        pattern: { cliparts: [], layers: [] },
        number_line: null,
        clock: null,
        protractor: null,
        divider: {
          type: "RECTANGLE", //Can be RECTANGLE or CIRCLE
          width: 200,
          height: 200,
          bg_color: "#ffff00", //Default yellow background color
          stroke_color: "#000000", //Default black stroke color
          polygon: {
            no_points_x_axis: 2,
            no_points_y_axis: 2,
          },
          circle: {
            no_points: 4,
            start_angle: 0,
          },
          answer: "1/2",
        },
        matching: null,
        mcq: null,
        ordering: null,
        descriptive: {
          answer: "",
          answer_not_exactly_same: false,
        },
        tf: {
          answer: true,
        },
        bar_chart: null,
        line_chart: null,
        venn_diagram: null,
        drag_box: null,
        color_grid: null,
        clipart_table: null,
        clipart_measurement: null,
        thermometer: null,
        shapes: null,
        ruler: null,
        grid_translator: null,
        angle_lines:null,
        line_creator:null,
        cut_spinner:null,
      },
    };
  },
  computed: {
    ...mapGetters([
      "fetchQuestions",
      "getTeacherToken",
      "fetchCurrentQuestion",
      "fetchCliparts",
      "fetchPictureLibraries",
    ]),
    currentPageCliparts() {
      return this.fetchCliparts.slice(
        (this.current_clipart_page - 1) * this.clipart_page_items,
        this.current_clipart_page * this.clipart_page_items
      );
    },
    currentLayerPage() {
      let page = null;
      if (this.new_question.layers[this.isOpen]) {
        const clipart = this.new_question.layers[this.isOpen].clipart;
        if (clipart) {
          const clipart_id = clipart._id;
          const clipart_index = this.fetchCliparts
            .map((clipart) => clipart._id)
            .indexOf(clipart_id);
          page = Math.floor(clipart_index / this.clipart_page_items) + 1;
        }
      }
      return page;
    },
  },
  methods: {
    ...mapActions([
      "loadCliparts",
      "loadPictureLibraries",
      "loadQuestionDetails",
      "uploadQuestionExplanationFile",
      "createQuestion",
      "editQuestion",
    ]),
    addClipartRowFromDrawer(clipart_obj) {
      let clo_row = returnNewCLOLayer();
      clo_row["type"] = "IMAGE";
      clo_row["picture_type"] = "C";
      clo_row["clipart"] = clipart_obj.clipart;
      clo_row["width"] = clipart_obj.width;
      clo_row["height"] = clipart_obj.height;
      this.new_question.custom_layout.objects.push(clo_row);
      this.$buefy.toast.open({
        message: "Added clipart to custom layout row",
        type: "is-success",
      });
    },
    changeExplanationFile() {
      if (
        this.$refs["new_explanation_file_inp"].files &&
        this.$refs["new_explanation_file_inp"].files[0]
      ) {
        let file = this.$refs["new_explanation_file_inp"].files[0];
        this.uploadQuestionExplanationFile({
          file,
          token: this.getTeacherToken,
        }).then((res) => {
          const url = res.data.url;
          let quill = this.$refs.myQuillEditor.quill;
          let length = quill.getSelection().index; // Get the cursor position
          quill.insertEmbed(length, "image", getImageURL(url)); // Insert image res.url for the image address returned by the server
          quill.setSelection(length + 1); // adjust the cursor to the end
        });
      }
    },
    plPath(id) {
      const current_pl = this.fetchPictureLibraries.find(
        (obj) => obj._id == id
      );
      return current_pl ? current_pl.file : null;
    },
    addCustomLayoutRow() {
      this.new_question.custom_layout.objects.push(returnNewCLOLayer());
    },
    addDragDropLayer() {
      //Check if the old layer has more than 5 items
      if (
        this.new_question.drag_drop.layers.length &&
        this.new_question.drag_drop.layers[0].number > 5
      ) {
        return this.$buefy.toast.open({
          message: "Current layer already has more than 5 items",
          type: "is-danger",
        });
      }

      if (this.new_question.drag_drop.layers.length == 2) {
        return this.$buefy.toast.open({
          message: "You cannot add more than 2 layers",
          type: "is-danger",
        });
      }
      const new_layer = Object.assign({}, this.new_layer_drag_drop);
      this.new_question.drag_drop.layers.push(new_layer);
      //Showing message to user
      this.$buefy.toast.open({
        message: "New Layer Added",
        type: "is-success",
      });
    },
    removeDragDropLayer(type, index) {
      if (type == "DRAGDROP") {
        this.new_question.drag_drop.layers.splice(index, 1);
      }
      //Showing message to user
      this.$buefy.toast.open({
        message: "Layer Removed",
        type: "is-success",
      });
    },

    selectClipartDragDrop(index, clipart) {
      this.new_question.drag_drop.layers[index].clipart = clipart;
    },
    selectPatternClipart(clipart) {
      this.new_question.pattern.cliparts.push(clipart);
    },
    removePatternClipart({ id, index }) {
      let allowed = true;
      for (let layer of this.new_question.pattern.layers) {
        for (let column of layer.columns) {
          if (column.clipart == index) {
            allowed = false;
            break;
          }

          if (!allowed) break;
        }
      }
      if (!allowed)
        return this.$buefy.toast.open({
          message: "Cannot remove this clipart as already used in pattern",
          type: "is-danger",
        });
      this.new_question.pattern.cliparts =
        this.new_question.pattern.cliparts.filter(
          (clipart) => clipart._id != id
        );
    },
    addPatternLayer() {
      let new_layer = { columns: [] };
      this.new_question.pattern.layers.push(new_layer);
    },
    addPatternColumn(index) {
      let new_column = Object.assign({}, this.pattern_column);
      if (new_column.type && new_column.clipart !== null) {
        this.new_question.pattern.layers[index].columns.push(new_column);
      }
    },
    removePatternLayer(row) {
      this.new_question.pattern.layers.splice(row, 1);
    },
    removePatternColumn(row, column) {
      this.new_question.pattern.layers[row].columns.splice(column, 1);
    },
    formatCLObjectsInitial(objects) {
      return objects.map((obj) => {
        let obj_new = Object.assign({}, obj);
        if (
          obj_new.type == "IMAGE" &&
          obj_new.picture_type == "P" &&
          obj_new.picture_library
        ) {
          //If picture library
          obj_new.picture_library = obj_new.picture_library._id;
        } else {
          obj_new.picture_library = null;
        }
        return obj_new;
      });
    },
    addQuestion() {
      let is_validate =
        this.new_question.question &&
        this.$refs.question_type.checkHtml5Validity();
      if (is_validate) {
        this.isLoading = true;
        let new_question = JSON.parse(JSON.stringify(this.new_question));
        // Changing the question from vue-quill
        new_question.question = formatHTMLString(new_question.question);
        new_question.question1 = formatHTMLString(new_question.question1);
        if (new_question.type == "MCQ" || new_question.type == "ORDERING") {
          delete new_question.descriptive;
          delete new_question.tf;
          delete new_question.drag_drop;
          delete new_question.pattern;
          delete new_question.divider;
          delete new_question.matching;

          let nested_ney = "mcq"; //Default key = mcq
          if (new_question.type == "ORDERING") {
            delete new_question.mcq;
            nested_ney = "ordering";
          } else if (new_question.type == "MCQ") {
            is_validate = new_question.mcq && new_question.mcq.options.length;
            delete new_question.ordering;

            //Checking the type of mcq
            if (
              new_question.mcq.type == "S" &&
              new_question.mcq.answer.length > 1
            ) {
              this.isLoading = false;
              return this.$buefy.toast.open({
                message: "Only single answer allowed for Single answer type",
                type: "is-danger",
              });
            }

            // Deleting custom layout if has_custom_layout is false
            if (!this.new_question.mcq.has_custom_layout) {
              delete new_question.custom_layout;
            }
          }

          //Changing the structure of options
          new_question[nested_ney].options = new_question[
            nested_ney
          ].options.map((option) => {
            if (option.layers) {
              option.layers = option.layers.map((layer) => {
                layer.clipart = layer.clipart._id;
                return layer;
              });
            }
            if (option.thermometer && !option.thermometer.type) {
              option.thermometer = null;
            }

            if (option.protractor && !option.protractor.template) {
              option.protractor = null;
            }

            // Dealing with number line
            if (!option.number_line || !option.number_line.show) {
              option.number_line = null;
            }

            // Dealing with shapes
            if (
              !option.shapes ||
              !option.shapes.num_rows ||
              !option.shapes.num_cols
            ) {
              option.shapes = null;
            }

            // Dealing with tables
            if(option.tables){
              option.tables = option.tables.map((t) => {
                for (let row of t.data) {
                  for (let column of row) {
                    if (
                      column.type == "IMAGE" &&
                      column.picture_type == "C" &&
                      column.clipart
                    ) {
                      //If clipart type
                      column.clipart = column.clipart._id;
                    }

                    //If clipart layer type
                    if (
                      column.type == "IMAGE" &&
                      column.layers &&
                      column.layers.length
                    ) {
                      //If clipart type
                      column.layers = column.layers.map((layer) => {
                        layer.clipart = layer.clipart._id;
                        return layer;
                      });
                    }

                    if(column.type == "TEXT"){
                      column.clipart = null
                      column.picture_library = null
                      column.layers = []
                    }
                  }
                }
                return t;
              });
            }
            

            if (option.custom_layout) {
              option.custom_layout.objects = option.custom_layout.objects.map(
                (object) => {
                  if (
                    object.type == "IMAGE" &&
                    object.picture_type == "C" &&
                    object.clipart
                  ) {
                    //If clipart type
                    object.clipart = object.clipart._id;
                  }
                  return object;
                }
              );
            }

            return option;
          });
          new_question[nested_ney] = JSON.stringify(new_question[nested_ney]);
        } else if (new_question.type == "DQ") {
          delete new_question.mcq;
          delete new_question.tf;
          delete new_question.custom_layout;
          delete new_question.drag_drop;
          delete new_question.pattern;
          delete new_question.ordering;
          delete new_question.divider;
          delete new_question.matching;
          new_question.descriptive = JSON.stringify(new_question.descriptive);
        } else if (new_question.type == "PATTERN") {
          delete new_question.mcq;
          delete new_question.tf;
          delete new_question.custom_layout;
          delete new_question.drag_drop;
          delete new_question.descriptive;
          delete new_question.ordering;
          delete new_question.divider;
          delete new_question.matching;
          new_question.pattern.cliparts = new_question.pattern.cliparts.map(
            (clipart) => clipart._id
          );
          new_question.pattern = JSON.stringify(new_question.pattern);
        } else if (new_question.type == "TF") {
          delete new_question.descriptive;
          delete new_question.mcq;
          delete new_question.custom_layout;
          delete new_question.drag_drop;
          delete new_question.pattern;
          delete new_question.ordering;
          delete new_question.divider;
          delete new_question.matching;
          new_question.tf = JSON.stringify(new_question.tf);
        } else if (new_question.type == "DIVIDER") {
          delete new_question.descriptive;
          delete new_question.mcq;
          delete new_question.custom_layout;
          delete new_question.drag_drop;
          delete new_question.pattern;
          delete new_question.ordering;
          delete new_question.tf;
          delete new_question.matching;
          new_question.divider = JSON.stringify(new_question.divider);
        } else if (new_question.type == "MATCHING") {
          delete new_question.descriptive;
          delete new_question.mcq;
          delete new_question.custom_layout;
          delete new_question.drag_drop;
          delete new_question.pattern;
          delete new_question.ordering;
          delete new_question.tf;
          delete new_question.divider;
          new_question.matching = JSON.stringify(new_question.matching);
        } else if (new_question.type == "DRAGDROP") {
          delete new_question.descriptive;
          delete new_question.mcq;
          delete new_question.custom_layout;
          delete new_question.tf;
          delete new_question.pattern;
          delete new_question.ordering;
          delete new_question.divider;
          delete new_question.matching;
          //Check if the old layer has more than 5 items
          if (
            this.new_question.drag_drop.layers.length == 2 &&
            (this.new_question.drag_drop.layers[0].number > 5 ||
              this.new_question.drag_drop.layers[1].number > 5)
          ) {
            this.isLoading = false;
            return this.$buefy.toast.open({
              message: "Only 5 items allowed in a Drag & Drop layer",
              type: "is-danger",
            });
          }

          new_question.drag_drop.layers = new_question.drag_drop.layers.map(
            (layer) => {
              layer.clipart = layer.clipart._id;
              return layer;
            }
          );
          new_question.drag_drop = JSON.stringify(new_question.drag_drop);
        }

        // Special case for custom layout
        if (
          new_question.type == "CL" ||
          (this.new_question.mcq && this.new_question.mcq.has_custom_layout)
        ) {
          delete new_question.descriptive;
          delete new_question.tf;
          delete new_question.drag_drop;
          delete new_question.pattern;
          delete new_question.ordering;
          delete new_question.divider;
          delete new_question.matching;
          // Deleting mcq only if custom layout type
          if (new_question.type == "CL") {
            delete new_question.mcq;
          }

          new_question.custom_layout.objects =
            new_question.custom_layout.objects.map((object) => {
              if (
                object.type == "IMAGE" &&
                object.picture_type == "C" &&
                object.clipart
              ) {
                //If clipart type
                object.clipart = object.clipart._id;
              }
              return object;
            });
          new_question.custom_layout = JSON.stringify(
            new_question.custom_layout
          );
        }

        // Process explanation_custom_layout if it exists
        if (new_question.explanation_custom_layout && new_question.explanation_custom_layout.objects) {
          new_question.explanation_custom_layout.objects =
            new_question.explanation_custom_layout.objects.map((object) => {
              if (
                object.type == "IMAGE" &&
                object.picture_type == "C" &&
                object.clipart
              ) {
                //If clipart type
                object.clipart = object.clipart._id;
              }
              return object;
            });
          new_question.explanation_custom_layout = JSON.stringify(
            new_question.explanation_custom_layout
          );
        }

        //Adding skill for this question
        new_question["skill"] = this.skill_id;
        //CHanging the structure of layers
        new_question.layers = new_question.layers.map((layer) => {
          layer.clipart = layer.clipart._id;
          if (layer.overlay_clipart) {
            layer.overlay_clipart = layer.overlay_clipart._id;
          }
          return layer;
        });

        //Changing the structure of stories
        new_question.stories = new_question.stories.map((layer) => {
          layer.picture_library = layer.picture_library._id;
          return layer;
        });

        //Changing the structure of bar chart
        if (new_question.bar_chart) {
          new_question.bar_chart.properties =
            new_question.bar_chart.properties.map((layer) => {
              if (layer.clipart && layer.clipart.clipart) {
                layer.clipart.clipart = layer.clipart.clipart._id;
              }
              return layer;
            });
        }
        new_question.bar_chart = JSON.stringify(new_question.bar_chart);

        //Changing the structure of Line Chart
        if (new_question.line_chart) {
          new_question.line_chart.properties =
            new_question.line_chart.properties.map((layer) => {
              if (layer.clipart && layer.clipart.clipart) {
                layer.clipart.clipart = layer.clipart.clipart._id;
              }
              return layer;
            });
        }
        new_question.line_chart = JSON.stringify(new_question.line_chart);

        //Changing the structure of Venn Diagram
        if (new_question.venn_diagram && new_question.venn_diagram.properties.length
        ) {
          new_question.venn_diagram.properties =
            new_question.venn_diagram.properties.map((layer) => {
              if (layer.clipart && layer.clipart.clipart) {
                layer.clipart.clipart = layer.clipart.clipart._id;
              }
              return layer;
            });
        } else {
          new_question.venn_diagram = null;
        }
        new_question.venn_diagram = JSON.stringify(new_question.venn_diagram);

        //Changing the structure of Drag Box
        new_question.drag_box = JSON.stringify(new_question.drag_box);

        //Changing the structure of Clipart Measurement
        if (
          new_question.clipart_measurement &&
          new_question.clipart_measurement.num_images
        ) {
          if (new_question.clipart_measurement.clipart) {
            new_question.clipart_measurement.clipart.clipart =
              new_question.clipart_measurement.clipart.clipart._id;
          }
          if (new_question.clipart_measurement.multi_clipart) {
            new_question.clipart_measurement.multi_clipart.clipart =
              new_question.clipart_measurement.multi_clipart.clipart._id;
          }
        } else {
          new_question.clipart_measurement = null;
        }
        new_question.clipart_measurement = JSON.stringify(
          new_question.clipart_measurement
        );

        //Changing the structure of Color Grid
        if (
          new_question.color_grid &&
          !new_question.color_grid.num_rows &&
          !new_question.color_grid.num_columns
        ) {
          new_question.color_grid = null;
        }
        new_question.color_grid = JSON.stringify(new_question.color_grid);
        
        //Changing the structure of Grid Translator
        if(!new_question.grid_translator || !new_question.grid_translator.letter){
          new_question.grid_translator=null;
        }
        new_question.grid_translator=JSON.stringify(new_question.grid_translator);

        //Changing the structure of Angle Lines
        if(!new_question.angle_lines || (!new_question.angle_lines.angle_start && !new_question.angle_lines.angle_between)){
          new_question.angle_lines=null;
        }
        new_question.angle_lines=JSON.stringify(new_question.angle_lines);

        //Changing the structure of Line Creator
        if(!new_question.line_creator || !new_question.line_creator.lines_cordinates || !new_question.line_creator.lines_cordinates.length){
          new_question.line_creator=null;
        }
        new_question.line_creator=JSON.stringify(new_question.line_creator);

        //Changing the structure of Cut Spinner
        if(!new_question.cut_spinner || (!new_question.cut_spinner.circle_angle && !new_question.cut_spinner.spinner_angle)){
          new_question.cut_spinner=null;
        }
        new_question.cut_spinner=JSON.stringify(new_question.cut_spinner);

        //Changing the structure of Clipart Table
        if (new_question.clipart_table && new_question.clipart_table.properties.length) {
          new_question.clipart_table.properties =
            new_question.clipart_table.properties.map((layer) => {
              if (layer.clipart && layer.clipart.clipart) {
                layer.clipart.clipart = layer.clipart.clipart._id;
              }
              return layer;
            });
        } else {
          new_question.clipart_table = null;
        }
        new_question.clipart_table = JSON.stringify(new_question.clipart_table);

        //Changing the structure of Thermometer
        if (new_question.thermometer && !new_question.thermometer.type) {
          new_question.thermometer = null;
        }
        new_question.thermometer = JSON.stringify(new_question.thermometer);

        //Changing the structure of Shapes
        if (!new_question.shapes || !new_question.shapes.num_rows || !new_question.shapes.num_cols) {
          new_question.shapes = null;
        }
        new_question.shapes = JSON.stringify(new_question.shapes);

        //Changing the structure of Ruler
        if (new_question.ruler && !new_question.ruler.type) {
          new_question.ruler = null;
        }
        new_question.ruler = JSON.stringify(new_question.ruler);

        // Changing the structure of tables
        new_question.tables = new_question.tables.map((table) => {
          for (let row of table.data) {
            for (let column of row) {
              if (
                column.type == "IMAGE" &&
                column.picture_type == "C" &&
                column.clipart
              ) {
                //If clipart type
                column.clipart = column.clipart._id;
              }

              //If clipart layer type
              if (
                column.type == "IMAGE" &&
                column.layers &&
                column.layers.length
              ) {
                //If clipart type
                column.layers = column.layers.map((layer) => {
                  layer.clipart = layer.clipart._id;
                  return layer;
                });
              }

              if(column.type == "TEXT"){
                column.clipart = null
                column.picture_library = null
                column.layers = []
              }
            }
          }
          return table;
        });

        // If clock type is selected, allow or else remove clock
        if (new_question.clock && new_question.clock.type) {
          new_question.clock = JSON.stringify(new_question.clock);
        } else {
          delete new_question.clock;
        }

        // If protractor is selected, allow or else remove protractor
        if (new_question.protractor && new_question.protractor.template) {
          new_question.protractor = JSON.stringify(new_question.protractor);
        } else {
          delete new_question.protractor;
        }

        //Removing secondary question positition key if default (null)
        if (!new_question.question1_position) {
          delete new_question.question1_position;
        }

        // Dealing with number line
        if (!new_question.number_line || !new_question.number_line.show) {
          new_question.number_line = null;
        }

        if (this.question_id) {
          //Creating a new question
          this.editQuestion({
            id: this.question_id,
            question: new_question,
            token: this.getTeacherToken,
          })
            .then(() => {
              //Stopping the loading icon
              this.isLoading = false;
              //Disabling the modal
              this.$emit("over");
              //Showing message to user
              this.$buefy.toast.open({
                message: "Question updated successfully",
                type: "is-success",
              });
            })
            .catch((err) => {
              //Stopping the loading icon
              this.isLoading = false;
              //Showing message to user
              this.$buefy.toast.open({
                message: err.response.data,
                type: "is-danger",
              });
            });
        } else {
          //Creating a new question

          this.createQuestion({
            question: new_question,
            token: this.getTeacherToken,
          })
            .then(() => {
              //Stopping the loading icon
              this.isLoading = false;
              //Disabling the modal
              this.$emit("over");
              //Showing message to user
              this.$buefy.toast.open({
                message: "Question added successfully",
                type: "is-success",
              });
            })
            .catch((err) => {
              //Stopping the loading icon
              this.isLoading = false;
              //Showing message to user
              this.$buefy.toast.open({
                message: err.response.data,
                type: "is-danger",
              });
            });
        }
      }
    },
  },
  async created() {
    this.loadCliparts({ token: this.getTeacherToken });
    this.loadPictureLibraries({ token: this.getTeacherToken });
    if (this.question_id) {
      this.isLoading = true;
      try {
        await this.loadQuestionDetails({
          token: this.getTeacherToken,
          id: this.question_id,
        });

        for (let key in this.new_question) {
          if (this.fetchCurrentQuestion[key]) {
            this.new_question[key] = this.fetchCurrentQuestion[key];

            // If Custom Layout
            if (key == "custom_layout") {
              this.$set(
                this.new_question[key],
                "allowed_height",
                this.new_question[key].parent_height
              );
              this.$set(
                this.new_question[key],
                "allowed_width",
                this.new_question[key].parent_width
              );

              // CL Objects
              const new_cl_objects = this.formatCLObjectsInitial(
                this.new_question[key].objects
              );

              this.$set(this.new_question[key], "objects", new_cl_objects);
            }
            
            // If Explanation Custom Layout
            if (key == "explanation_custom_layout") {
              this.$set(
                this.new_question[key],
                "allowed_height",
                this.new_question[key].parent_height
              );
              this.$set(
                this.new_question[key],
                "allowed_width",
                this.new_question[key].parent_width
              );

              // ECL Objects
              const new_ecl_objects = this.formatCLObjectsInitial(
                this.new_question[key].objects
              );

              this.$set(this.new_question[key], "objects", new_ecl_objects);
            }
            
            if (key == "drag_drop") {
              this.new_question[key].background =
                this.new_question[key].background._id;
              if (this.new_question[key].background1) {
                this.new_question[key].background1 =
                  this.new_question[key].background1._id;
              }
            }
            if (["mcq", "ordering"].includes(key)) {
              if (!this.new_question[key].type) {
                this.new_question[key].type = "S";
              }

              let mcq_obj = JSON.parse(JSON.stringify(this.new_question[key]));
              for (let option_key in mcq_obj.options) {
                let option = mcq_obj.options[option_key];

                // Backfill styles for legacy options (esp. older ORDERING
                // questions) that were saved before the styles field existed.
                // Without this, SetMcq.vue crashes reading option.styles.font_size.
                if (!option.styles) {
                  option.styles = {
                    border_width: 2,
                    border_color: "#2acdc1",
                    font_color: "#fff",
                    font_size: 16,
                    text_align: "center",
                  };
                } else {
                  if (option.styles.border_width == null) option.styles.border_width = 2;
                  if (option.styles.border_color == null) option.styles.border_color = "#2acdc1";
                  if (option.styles.font_color == null) option.styles.font_color = "#fff";
                  if (option.styles.font_size == null) option.styles.font_size = 16;
                  if (option.styles.text_align == null) option.styles.text_align = "center";
                }

                if (option.custom_layout) {
                  option.custom_layout.allowed_height =
                    option.custom_layout.parent_height;
                  option.custom_layout.allowed_width =
                    option.custom_layout.parent_width;
                  option.custom_layout.objects = this.formatCLObjectsInitial(
                    option.custom_layout.objects
                  );
                }
              }
              this.$set(this.new_question, key, mcq_obj);
            }
            if (key == "number_line") {
              if (this.new_question[key]) {
                this.$set(this.new_question[key], "show", true);

                if (!this.new_question[key].arrows) {
                  this.$set(this.new_question[key], "arrows", []);
                }
                if (!this.new_question[key].numbers) {
                  this.$set(this.new_question[key], "numbers", []);
                }
              }
            }
          }
        }

        //If type is mcq
        if (this.new_question.type == "MCQ") {
          this.new_question.mcq.options = this.new_question.mcq.options.map(
            (option) => {
              if (!option.layer) {
                option.layer = Object.assign({}, this.new_layer);
              }
              return option;
            }
          );
        }
      } catch (err) {
        let message = err.response
          ? err.response.data
            ? err.response.data
            : err.response
          : err;
        this.$buefy.toast.open({
          message,
          type: "is-danger",
          duration: 2000,
        });
      }
      this.isLoading = false;
    }
  },
};
</script>

<style scoped>
/* Preserve whitespace in Quill editor */
.quiz-description-box .ql-editor {
  white-space: pre-wrap;
}

/* Also preserve whitespace in the editor content */
.quiz-description-box .ql-editor p,
.quiz-description-box .ql-editor div {
  white-space: pre-wrap;
}
</style>
